/* STAS — Countries master catalogue management.
 *
 * Lets STAS toggle blocked / active, mark popular destinations, and edit
 * trilingual names + search aliases. Changes are persisted immediately
 * per row (no global save button) so the office can react fast.
 */
import { useEffect, useMemo, useState } from "react";
import { Globe, Ban, CheckCircle2, Star, Save, Search, RefreshCw } from "lucide-react";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function CountriesAdminPage() {
  const { theme, lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showOnly, setShowOnly] = useState("all"); // all | blocked | popular | schengen
  const [editing, setEditing] = useState(null);    // code being edited
  const [saving, setSaving] = useState({});
  const [err, setErr] = useState("");

  const isRoot = !!(user?.is_root || user?.role?.is_root || user?.role?.code === "stas");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/countries");
      setRows(data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || (isAr ? "تعذّر تحميل الدول" : "Failed to load countries"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = async (code, body) => {
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      const { data } = await api.patch(`/admin/countries/${code}`, body);
      setRows((r) => r.map((x) => x.code === code ? data : x));
    } catch (e) {
      alert(e?.response?.data?.detail || "save failed");
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[code]; return c; });
    }
  };

  const reseed = async () => {
    if (!window.confirm(isAr ? "إعادة بذر القائمة الأساسية؟ ستحدّث الأسماء والمرادفات الافتراضية فقط (لن تُلمس قرارات الحظر / التفعيل)." : "Re-seed master catalogue? Refreshes default names + aliases only; preserves block / active toggles.")) return;
    try {
      await api.post("/admin/countries/seed");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "reseed failed");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((c) => {
      if (showOnly === "blocked" && !c.is_blocked) return false;
      if (showOnly === "popular" && !c.is_popular) return false;
      if (showOnly === "schengen" && !c.is_schengen) return false;
      if (!q) return true;
      const haystack = [
        c.code, c.name_ar, c.name_en, c.name_ur,
        ...(c.aliases_ar || []), ...(c.aliases_en || []), ...(c.aliases_ur || []),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search, showOnly]);

  if (!isRoot) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        {isAr ? "هذه الصفحة مخصصة لـ STAS فقط." : "STAS only."}
      </div>
    );
  }

  return (
    <div style={{ padding: 24, color: theme.text, fontFamily: "inherit" }} data-testid="countries-admin">
      <header style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Globe size={26} color={theme.gold} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>
            {isAr ? "إدارة الدول" : "Countries"}
          </h1>
          <div style={{ fontSize: 12, opacity: 0.65 }}>
            {isAr
              ? `${rows.length} دولة · ${rows.filter(r=>r.is_blocked).length} محظورة · ${rows.filter(r=>r.is_popular).length} مميّزة`
              : `${rows.length} countries · ${rows.filter(r=>r.is_blocked).length} blocked · ${rows.filter(r=>r.is_popular).length} popular`}
          </div>
        </div>
        <button onClick={reseed} style={btn(theme, "ghost")} data-testid="btn-reseed">
          <RefreshCw size={14} /> {isAr ? "إعادة بذر" : "Re-seed"}
        </button>
      </header>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          background: theme.surface, padding: "6px 10px", borderRadius: 10,
          border: `1px solid ${theme.border}`, flex: 1, minWidth: 240,
        }}>
          <Search size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? "ابحث بالكود أو الاسم أو المرادف…" : "Search code, name or alias…"}
            style={{ background: "transparent", border: "none", outline: "none", color: theme.text, flex: 1, fontSize: 13 }}
            data-testid="countries-admin-search"
          />
        </div>
        {[
          { k: "all", ar: "الكل", en: "All" },
          { k: "popular", ar: "مميّزة", en: "Popular" },
          { k: "schengen", ar: "شنغن", en: "Schengen" },
          { k: "blocked", ar: "محظورة", en: "Blocked" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setShowOnly(f.k)}
            style={btn(theme, showOnly === f.k ? "primary" : "ghost")}
            data-testid={`countries-admin-filter-${f.k}`}
          >
            {isAr ? f.ar : f.en}
          </button>
        ))}
      </div>

      {err && <div style={{ color: "#dc2626", marginBottom: 10 }}>{err}</div>}

      <div style={{ overflowX: "auto", border: `1px solid ${theme.border}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: theme.surface, position: "sticky", top: 0 }}>
            <tr>
              <th style={th}>{isAr ? "العلم" : "Flag"}</th>
              <th style={th}>{isAr ? "الكود" : "Code"}</th>
              <th style={th}>{isAr ? "عربي" : "Arabic"}</th>
              <th style={th}>{isAr ? "إنجليزي" : "English"}</th>
              <th style={th}>{isAr ? "أردو" : "Urdu"}</th>
              <th style={th}>{isAr ? "مرادفات" : "Aliases"}</th>
              <th style={th}>{isAr ? "حظر" : "Block"}</th>
              <th style={th}>{isAr ? "مميّزة" : "Popular"}</th>
              <th style={th}>{isAr ? "مفعّلة" : "Active"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", opacity: 0.6 }}>…</td></tr>}
            {!loading && filtered.map((c) => (
              <CountryRow
                key={c.code}
                c={c}
                theme={theme}
                isAr={isAr}
                editing={editing === c.code}
                onEdit={() => setEditing(c.code)}
                onSaveEdit={(body) => { patch(c.code, body); setEditing(null); }}
                onCancelEdit={() => setEditing(null)}
                saving={!!saving[c.code]}
                onToggleBlock={() => patch(c.code, { is_blocked: !c.is_blocked })}
                onTogglePopular={() => patch(c.code, { is_popular: !c.is_popular })}
                onToggleActive={() => patch(c.code, { is_active: !c.is_active })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CountryRow({ c, theme, isAr, editing, onEdit, onSaveEdit, onCancelEdit, saving, onToggleBlock, onTogglePopular, onToggleActive }) {
  // The draft is initialised lazily — only the FIRST time the row enters
  // edit mode. When the parent commits a save, `editing` flips back to
  // false and the next click on the row resets `draft` from fresh props.
  const [draft, setDraft] = useState(null);
  const startEdit = () => {
    setDraft({
      name_ar: c.name_ar, name_en: c.name_en, name_ur: c.name_ur,
      aliases_ar: (c.aliases_ar || []).join(", "),
      aliases_en: (c.aliases_en || []).join(", "),
      aliases_ur: (c.aliases_ur || []).join(", "),
    });
    onEdit();
  };

  if (editing && draft) {
    return (
      <tr style={{ background: "rgba(212,175,55,0.06)", borderTop: `1px solid ${theme.border}` }}>
        <td style={td}><span style={{ fontSize: 22 }} className="vta-flag vta-flag-wave">{c.flag}</span></td>
        <td style={td}><code>{c.code}</code></td>
        <td style={td}><input value={draft.name_ar} onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} style={inp(theme)} /></td>
        <td style={td}><input value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} style={inp(theme)} /></td>
        <td style={td}><input value={draft.name_ur} onChange={(e) => setDraft({ ...draft, name_ur: e.target.value })} style={inp(theme)} /></td>
        <td style={td} colSpan={1}>
          <input placeholder="ar (بفواصل)" value={draft.aliases_ar} onChange={(e) => setDraft({ ...draft, aliases_ar: e.target.value })} style={inp(theme)} />
          <input placeholder="en (commas)" value={draft.aliases_en} onChange={(e) => setDraft({ ...draft, aliases_en: e.target.value })} style={{ ...inp(theme), marginTop: 4 }} />
          <input placeholder="ur" value={draft.aliases_ur} onChange={(e) => setDraft({ ...draft, aliases_ur: e.target.value })} style={{ ...inp(theme), marginTop: 4 }} />
        </td>
        <td colSpan={3} style={{ ...td, textAlign: "center" }}>
          <button
            onClick={() => onSaveEdit({
              name_ar: draft.name_ar, name_en: draft.name_en, name_ur: draft.name_ur,
              aliases_ar: draft.aliases_ar.split(",").map((s) => s.trim()).filter(Boolean),
              aliases_en: draft.aliases_en.split(",").map((s) => s.trim()).filter(Boolean),
              aliases_ur: draft.aliases_ur.split(",").map((s) => s.trim()).filter(Boolean),
            })}
            style={btn(theme, "primary")}
            data-testid={`country-row-save-${c.code}`}
          >
            <Save size={12} /> {isAr ? "حفظ" : "Save"}
          </button>
          {" "}
          <button onClick={onCancelEdit} style={btn(theme, "ghost")}>
            {isAr ? "إلغاء" : "Cancel"}
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderTop: `1px solid ${theme.border}`, opacity: c.is_active ? 1 : 0.5 }} data-testid={`country-row-${c.code}`}>
      <td style={td}><span style={{ fontSize: 22 }} className="vta-flag vta-flag-wave">{c.flag}</span></td>
      <td style={{ ...td, fontFamily: "monospace", fontWeight: 700 }}>
        {c.code}
        {c.is_schengen && <div style={{ fontSize: 9, color: "#2563eb", marginTop: 2 }}>Schengen</div>}
      </td>
      <td style={td} onClick={startEdit} role="button">{c.name_ar}</td>
      <td style={td} onClick={startEdit} role="button">{c.name_en}</td>
      <td style={td} onClick={startEdit} role="button">{c.name_ur}</td>
      <td style={{ ...td, maxWidth: 220, fontSize: 11, opacity: 0.75 }} onClick={startEdit} role="button">
        {[...(c.aliases_ar||[]), ...(c.aliases_en||[]), ...(c.aliases_ur||[])].join(" · ") || "—"}
      </td>
      <td style={{ ...td, textAlign: "center" }}>
        <button
          onClick={onToggleBlock}
          disabled={saving}
          style={btn(theme, c.is_blocked ? "danger" : "ghost")}
          data-testid={`country-block-${c.code}`}
        >
          {c.is_blocked ? <Ban size={12} /> : <CheckCircle2 size={12} />}
          {c.is_blocked ? (isAr ? "محظورة" : "Blocked") : (isAr ? "مسموح" : "Allowed")}
        </button>
      </td>
      <td style={{ ...td, textAlign: "center" }}>
        <button
          onClick={onTogglePopular}
          disabled={saving}
          style={btn(theme, c.is_popular ? "gold" : "ghost")}
          data-testid={`country-popular-${c.code}`}
        >
          <Star size={12} fill={c.is_popular ? "#d4af37" : "none"} />
        </button>
      </td>
      <td style={{ ...td, textAlign: "center" }}>
        <button
          onClick={onToggleActive}
          disabled={saving}
          style={btn(theme, c.is_active ? "ghost" : "danger")}
          data-testid={`country-active-${c.code}`}
        >
          {c.is_active ? "ON" : "OFF"}
        </button>
      </td>
    </tr>
  );
}

const th = { padding: "10px 8px", fontSize: 11, textAlign: "start", fontWeight: 800, opacity: 0.8, textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "10px 8px", verticalAlign: "middle" };
const inp = (t) => ({
  width: "100%", padding: "5px 7px", borderRadius: 6,
  background: t.bg, color: t.text, border: `1px solid ${t.border}`,
  fontSize: 12, fontFamily: "inherit",
});
const btn = (t, tone = "ghost") => {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 800,
    cursor: "pointer", border: `1px solid ${t.border}`,
    background: t.bg, color: t.text, transition: "background 120ms",
  };
  if (tone === "primary") return { ...base, background: t.gold, color: "#1a1a1a", borderColor: t.gold };
  if (tone === "danger")  return { ...base, background: "rgba(220,38,38,0.10)", color: "#dc2626", borderColor: "rgba(220,38,38,0.5)" };
  if (tone === "gold")    return { ...base, background: "rgba(212,175,55,0.18)", color: t.gold || "#d4af37", borderColor: t.gold || "rgba(212,175,55,0.6)" };
  return base;
};
